import {Box, HStack, Image, Skeleton, SkeletonText, Stack, Text, VStack} from "@chakra-ui/react";
import {useParams} from "react-router-dom";
import {useQuery} from "@tanstack/react-query";
import {getProductsById} from "../../../../../data/api/Customer";
import {ProductModelNew} from "../../../../../data/model/Product";
import {useEffect, useMemo, useState} from "react";
import {BaseContentPage} from "../../../../../components/molecules/BaseContentPage/BaseContentPage";
import {BaseResponse} from "../../../../../data/model/BaseResponse";
import {KeyValueRow} from "../../../../../components/atoms/AddTableRow/AddTableRow";
import {getFormattedDataFromTimestamp} from "../../../../../utils/Utils";

export const ProductDetailsPage = () => {
    const {id} = useParams();
    const response = useQuery({queryKey: ["get-products", id], queryFn: () => getProductsById({id})});

    const {data, isLoading} = response || {};
    const productResponse = data?.data as (BaseResponse<ProductModelNew>) || {};
    const product = productResponse.data || {};
    const {mediaList: images} = product || {};

    const imagesSrc = useMemo(() => images?.map(value => value.url) || [], [images]);

    const [selectedImage, setSelectedImage] = useState<string | undefined>(undefined)
    const [imagesArray, setImagesArray] = useState<(string | undefined)[] | undefined>([]);

    useEffect(() => {
        if (imagesSrc !== undefined) {
            if (imagesSrc[0] !== undefined)
                setSelectedImage(imagesSrc[0]);
            setImagesArray(imagesSrc.slice(1));
        }
    }, [imagesSrc]);

    return <BaseContentPage>
        <HStack w={"100%"} spacing={0} backgroundColor={"surfaceContainer.light"}
                borderRadius={"16px"} mx={"24px"} mb={"24px"} py={"48px"}>
            <VStack h={"100%"} w={"50%"} justifyContent={"center"} alignItems={"center"}>
                {isLoading ? <ProductDetailLoading/> : <ProductDetailSection product={product}/>}
            </VStack>
            {isLoading && <VStack h={"100%"} w={"50%"} maxW={"50%"} alignItems={"center"} justifyContent={"center"}
                                  spacing={"24px"}>
                <Skeleton borderRadius={"16px"} w={"70%"} aspectRatio={1}/>
            </VStack>}
            {!isLoading && <VStack h={"100%"} w={"50%"} maxW={"50%"} alignItems={"center"} justifyContent={"center"}
                                   spacing={"24px"}>
                {selectedImage &&
                    <Image borderRadius={"16px"} objectFit={"contain"}
                           boxShadow={"1px 1px 10px rgba(0,0,0,0.1)"} src={selectedImage} h={"412px"}/>}
                {(imagesArray?.length || 0) > 1 &&
                    <Stack direction={"row"} flexWrap={"wrap"} w={"100%"} alignItems={"center"}
                           justifyContent={"center"}
                           p={"24px"} spacing={"16px"}>
                        {imagesArray?.map((item, index) => {
                            return <Box w={"64px"} h={"64px"} boxShadow={"1px 1px 10px rgba(0,0,0,0.1)"}
                                        onClick={() => setSelectedImage(item)}
                                        backgroundColor={"white"}
                                        borderRadius={"16px"} cursor={"pointer"} overflow={"hidden"} minW={"64px"}
                                        _hover={{
                                            boxShadow: "1px 1px 10px rgba(0, 108,75, 0.8)"
                                        }}><Image
                                key={`product_${id}_image_${index}`} objectFit={"cover"} src={item}/></Box>
                        })}
                    </Stack>}
            </VStack>}
        </HStack>
    </BaseContentPage>
}

const ProductDetailLoading = () => {
    return <Stack w={"100%"} px={"15%"} py={"48px"} spacing={"8px"} overflowY={"auto"}>
        <Skeleton h={"24px"} w={"25%"} fontWeight={"bold"}/>
        <Skeleton h={"48px"} w={"75%"} fontWeight={"bold"}/>
        <Skeleton h={"24px"} w={"35%"}/>

        <Skeleton h={"24px"} w={"20%"} mt={"24px"}/>
        <Skeleton h={"72px"} w={"15%"} mt={"8px"} ms={"8px"}/>

        <SkeletonText skeletonHeight={"24px"} noOfLines={10} fontSize={"16px"} mt={"24px"}/>
    </Stack>
}

const ProductDetailSection = ({product}: { product?: ProductModelNew }) => {
    const {
        name,
        description,
        sku,
        categories,
        createdAt: dateCreated,
        updatedAt,
        producer
    } = product || {};

    const categoriesName = useMemo(() => categories?.map(data => data.name)?.join(", "), [categories]);
    const {name: producerName} = producer || {};

    return <Stack w={"100%"} px={"15%"} py={"48px"} spacing={"8px"} overflowY={"auto"}>
        <Text fontSize={"16px"} fontWeight={"bold"}>{categoriesName}</Text>
        <Text fontSize={"32px"} color={"primary.400"} fontWeight={"bold"}>{name}</Text>
        <Text fontSize={"16px"}>{producerName}</Text>
        <KeyValueRow props={{mt: '24px'}} name={"SKU"}>{sku}</KeyValueRow>
        <KeyValueRow name={"Creato il"}>{getFormattedDataFromTimestamp(dateCreated)}</KeyValueRow>
        {updatedAt && <KeyValueRow name={"Modificato il"}>{getFormattedDataFromTimestamp(updatedAt)}</KeyValueRow>}

        <Text dangerouslySetInnerHTML={{__html: description || ""}} mt={"24px"}/>
    </Stack>
}
